import { Box, InputAdornment, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { NumericTextField } from '../../../components/forms/NumericTextField';
import type { DirectShearStage } from '../../../types/directShear';
import { stageFields } from '../config/directShearFields';

interface StageParametersGridProps {
  stages: DirectShearStage[];
  onChange: (stageIndex: number, field: string, value: number | null) => void;
  readOnly?: boolean;
}

const SHORT_LABELS: Record<string, string> = {};

// Fields to hide from the stage grid
const HIDDEN_FIELDS = new Set(['verticalStressOffset']);

export const StageParametersGrid = ({ stages, onChange, readOnly = false }: StageParametersGridProps) => {
  const theme = useTheme();

  // BUG-2026-0210: receives the parsed number from NumericTextField (the
  // draft text stays in the input, so "1.00" no longer collapses to "1").
  const handleChange = (stageIdx: number, field: string, value: number | null) => {
    onChange(stageIdx, field, value);
    // Auto-calculate Load = Area × Stress (kN = m² × kPa)
    if (field === 'area' || field === 'initialStress') {
      const stage = stages[stageIdx] as unknown as Record<string, unknown>;
      const area = field === 'area' ? value : (stage.area as number | null);
      const stress = field === 'initialStress' ? value : (stage.initialStress as number | null);
      if (area != null && stress != null && area > 0 && stress > 0) {
        onChange(stageIdx, 'initialLoad', area * stress);
      }
    }
  };

  const cols = `100px repeat(${stages.length}, 1fr)`;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, mb: 2, borderRadius: 2,
        border: `1px solid ${alpha(theme.border.color, 0.35)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, fontSize: '0.95rem' }}>
        Stage-Specific Parameters
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
        {/* Header row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 1, minWidth: 600, mb: 0.5 }}>
          <Box />
          {stages.map((stage, idx) => (
            <Typography
              key={`header-${stage.stageNumber}`}
              variant="caption"
              sx={{
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '0.78rem',
                color: theme.customColors.appBar,
                backgroundColor: idx % 2 === 0
                  ? alpha(theme.customColors.appBar, 0.06)
                  : alpha(theme.customColors.appBar, 0.03),
                borderRadius: 1,
                py: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Stage {stage.stageNumber}
            </Typography>
          ))}
        </Box>

        {/* Field rows */}
        {stageFields.filter((f) => !HIDDEN_FIELDS.has(f.key)).map((field) => (
          <Box
            key={field.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: cols,
              gap: 1,
              minWidth: 600,
              alignItems: 'center',
              height: 44,
              borderBottom: `1px solid ${alpha(theme.border.color, 0.15)}`,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ fontSize: '0.72rem', pr: 1 }}
            >
              {SHORT_LABELS[field.key] ?? field.label}
            </Typography>
            {stages.map((stage, idx) => {
              const isLoadField = field.key === 'initialLoad';
              const cell = (stage as unknown as Record<string, unknown>)[field.key];
              return (
              <NumericTextField
                key={`${field.key}-${stage.stageNumber}`}
                size="small"
                value={typeof cell === 'number' ? cell : null}
                onChange={(n) => !isLoadField && handleChange(idx, field.key, n)}
                disabled={readOnly}
                InputProps={{
                  readOnly: isLoadField,
                  endAdornment: field.unit ? (
                    <InputAdornment position="end">
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                        {field.unit}
                      </Typography>
                    </InputAdornment>
                  ) : undefined,
                }}
                sx={{
                  width: '100%',
                  backgroundColor: isLoadField
                    ? alpha(theme.palette.primary.main, 0.04)
                    : idx % 2 === 0
                      ? alpha(theme.palette.background.default, 0.5)
                      : 'transparent',
                  '& .MuiInputBase-input': {
                    py: 0.6, px: 1, fontSize: '0.8rem',
                    fontWeight: isLoadField ? 600 : 400,
                    color: isLoadField ? theme.palette.primary.main : undefined,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isLoadField
                      ? alpha(theme.palette.primary.main, 0.2)
                      : alpha(theme.border.color, 0.35),
                  },
                }}
              />
              );
            })}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};
